import React, { useMemo, useState } from "react";
import { Container, Row, Col, Card, Form, Button, Badge } from "react-bootstrap";
import { useSelector } from "react-redux";
import axiosInstance from "utils/AxiosInstance";
import { API } from "utils/Apiroutes";
import { FiUser, FiMail, FiShield, FiLock, FiActivity, FiClock } from "react-icons/fi";
import { GRADIENTS } from "utils/theme";

function Settings() {
  const { user } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  const userDetails = useMemo(
    () => ({
      name:
        user?.name ||
        user?.fullName ||
        user?.username ||
        user?.adminName ||
        "N/A",
      email: user?.email || user?.adminEmail || "N/A",
      role: user?.role || user?.userType || user?.type || "Admin",
    }),
    [user]
  );

  const appDetails = useMemo(
    () => ({
      loginStatus: localStorage.getItem("vendispot_token") ? "Active" : "Inactive",
    }),
    []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters";
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post(API.changePassword, {
        oldPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });

      const Swal = require('sweetalert2');
      Swal.fire('Success!', response?.data?.message || 'Password updated successfully!', 'success');
      
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
    } catch (error) {
      const Swal = require('sweetalert2');
      Swal.fire('Error!', error?.response?.data?.message || 'Failed to update password', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col md="12">
          <Card className="shadow border-0 rounded-3 overflow-hidden">
            <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0 fw-semibold">
                    <i className="fas fa-cog me-2"></i>
                    Settings
                  </h4>
                  <p className="mb-0 opacity-75 mt-1" style={{ fontSize: '0.9rem' }}>
                    Manage your account and security settings
                  </p>
                </div>
                <Badge
                  bg={appDetails.loginStatus === "Active" ? "success" : "danger"}
                  className="rounded-pill px-3 py-2"
                >
                  <FiActivity className="me-1" />
                  Session {appDetails.loginStatus}
                </Badge>
              </div>
            </div>

            <Card.Body className="p-4">
              <Row className="g-4 mb-4">
                <Col lg={6} md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon bg-info bg-opacity-10 text-info rounded-circle p-3 me-3">
                          <FiMail size={24} />
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="text-muted mb-1 fw-medium">Email Address</h6>
                          <h6 className="fw-semibold text-dark mb-0">{userDetails.email}</h6>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={6} md={6}>
                  <Card className="border-0 shadow-sm h-100">
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <div className="stat-icon bg-success bg-opacity-10 text-success rounded-circle p-3 me-3">
                          <FiShield size={24} />
                        </div>
                        <div className="flex-grow-1">
                          <h6 className="text-muted mb-1 fw-medium">Account Role</h6>
                          <h5 className="fw-bold text-dark mb-0">{userDetails.role}</h5>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 py-3 px-4">
                  <h5 className="fw-semibold mb-0">
                    <FiLock className="me-2 text-primary" />
                    Change Password
                  </h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md="4" sm="12">
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-medium">Current Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="currentPassword"
                            placeholder="Enter current password"
                            value={formData.currentPassword}
                            onChange={handleChange}
                            isInvalid={!!errors.currentPassword}
                            style={{ height: "44px" }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.currentPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md="4" sm="12">
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-medium">New Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="newPassword"
                            placeholder="Enter new password"
                            value={formData.newPassword}
                            onChange={handleChange}
                            isInvalid={!!errors.newPassword}
                            style={{ height: "44px" }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.newPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md="4" sm="12">
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-medium">Confirm Password</Form.Label>
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            placeholder="Confirm new password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            isInvalid={!!errors.confirmPassword}
                            style={{ height: "44px" }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 mt-3">
                      <Button
                        type="submit"
                        variant="primary"
                        className="px-4 fw-medium"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Update Password
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="px-4 fw-medium"
                        onClick={() => {
                          setFormData({
                            currentPassword: "",
                            newPassword: "",
                            confirmPassword: "",
                          });
                          setErrors({});
                        }}
                        disabled={isSubmitting}
                      >
                        <i className="fas fa-times me-2"></i>
                        Clear
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Settings;
